import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { orpc } from "@/lib/orpc";
import { useCanOrganizationWide, useFeature, usePolicy } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { CareersBrandingCard } from "./-components/crm/careers-branding-card";
import { describedBy } from "./-components/forms/field-parts";
import { FieldError } from "@/routes/_authenticated/_administration/settings/-components/forms/field-error";
import { OrganizationDetailsCard } from "./-components/account/organization-details-card";
import { SetupChecklistCard } from "./-components/checklists/setup-checklist-card";

export const Route = createFileRoute("/_authenticated/_administration/settings/organization")({
  beforeLoad: ({ context }) => guardRoute(context.me, "organization"),
  component: OrganizationSettingsPage,
});

/** The organization-wide settings the first-run wizard captures, made editable once onboarding is
 * over: who the account says it is, whether Medicaid modules exist for anybody in it, and what the
 * public careers page looks like. All three sit above the facility rather than inside one. */
function OrganizationSettingsPage() {
  const { subject } = usePolicy();
  const organizationId = subject.organization?.id ?? null;
  /* The organization's own record: the API asks for this organization-wide, never at a building. */
  const canUpdate = useCanOrganizationWide("organization:update");
  const hiring = useFeature("hiring");
  const queryClient = useQueryClient();
  const [name, setName] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  /* The tenant's own record, not the platform console's. `organizations.get` is guarded by
   * `platform:read`, which no organization role holds, so an administrator opening their own
   * settings was shown a permission error where the Medicaid switch belongs. */
  const organization = useQuery({
    ...orpc.administration.organizations.current.queryOptions({}),
    enabled: organizationId !== null,
  });

  const update = useMutation(
    orpc.administration.organizations.updateCurrent.mutationOptions({
      onSuccess: async () => {
        toast.success("Organization settings updated");
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: orpc.administration.organizations.key() }),
          /* Medicaid acceptance is part of the subject every screen authorises against, so the
           * session snapshot has to be refetched with it or the Medicaid modules keep their old
           * visibility until a reload. The name is in it too: it is what the header reads. */
          queryClient.invalidateQueries({ queryKey: orpc.auth.me.key() }),
        ]);
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  if (organizationId === null) {
    return (
      <>
        <PageHeader title="Organization" />
        <EmptyState
          title="No organization"
          description="Organization settings are not available for this account."
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Organization"
        meta="Organization-wide settings that affect every facility in your account."
      />

      {organization.isPending ? (
        <Skeleton className="h-64" />
      ) : organization.isError ? (
        <ReadFailed
          message="Could not load organization settings."
          error={organization.error}
          retrying={organization.isFetching}
          onRetry={() => void organization.refetch()}
        />
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Name</CardTitle>
              <CardDescription>
                What your account is called, everywhere it is named: the header, the careers page,
                the documents you send out.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <form
                className="grid gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  const next = (name ?? organization.data.name).trim();
                  if (next.length < 2) {
                    setNameError("Use at least 2 characters for the name.");
                    document.getElementById("organization-name")?.focus();
                    return;
                  }
                  setNameError(null);
                  update.mutate({ name: next });
                }}
              >
                <Label htmlFor="organization-name">Organization name</Label>
                <div className="flex flex-wrap gap-2">
                  <Input
                    id="organization-name"
                    className="min-w-64 flex-1"
                    value={name ?? organization.data.name}
                    disabled={!canUpdate}
                    autoComplete="off"
                    aria-invalid={nameError ? true : undefined}
                    aria-describedby={describedBy("organization-name", { error: nameError })}
                    onChange={(event) => setName(event.target.value)}
                  />
                  {canUpdate ? (
                    <Button type="submit" disabled={update.isPending}>
                      {update.isPending ? "Saving…" : "Save name"}
                    </Button>
                  ) : null}
                </div>
                <FieldError id="organization-name" error={nameError} />
              </form>
              <div className="bg-sunken border-border-soft text-muted-foreground flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-md border px-3 py-2 text-sm">
                <span>Web address</span>
                <span className="text-foreground min-w-0 font-mono break-all">
                  {organization.data.slug}
                </span>
                <span aria-hidden className="max-sm:hidden">
                  ,
                </span>
                <span>Links already sent out use it, so support changes it for you.</span>
              </div>
            </CardContent>
          </Card>

          <OrganizationDetailsCard organization={organization.data} canUpdate={canUpdate} />

          <Card>
            <CardHeader>
              <CardTitle>Medicaid</CardTitle>
              <CardDescription>
                Enables Medicaid-specific modules and reporting across your organization, where
                included in your plan.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-4">
                <div className="grid gap-1">
                  <Label htmlFor="accepts-medicaid">Accepts Medicaid</Label>
                  <p className="text-muted-foreground text-sm">
                    Turn this off for a private-pay-only organization.
                  </p>
                </div>
                <Switch
                  id="accepts-medicaid"
                  checked={organization.data.acceptsMedicaid}
                  disabled={!canUpdate || update.isPending}
                  onCheckedChange={(acceptsMedicaid) => update.mutate({ acceptsMedicaid })}
                />
              </div>
            </CardContent>
          </Card>

          {hiring ? (
            <CareersBrandingCard organization={organization.data} canUpdate={canUpdate} />
          ) : null}

          {/* Last: the way back to setup once it is hidden from the menu. */}
          <SetupChecklistCard />
        </>
      )}
    </>
  );
}
